import { ReactElement } from 'react';
import { SimpleButton } from '../../../../../../components/buttons/SimpleButton';
import { getCurrentUser, canScheduleForLocation } from '../../../../../../services/helper';
import { Course } from '../../../../../../types/course.type';
import { Lesson } from '../../../../../../types/lesson.type';
import { getCurrentDateOnly } from '../../../../../../utils/dateUtils';
import { getLessonInstructorDisplay } from '../../../../../../utils/lessonInstructorUtils';
import { lessonAssignmentActionFn } from './SessionDetail.types';
import './LessonScheduleActions.css';

const currentUser = getCurrentUser();

const canScheduleFor = (location_id: number) =>
  canScheduleForLocation(currentUser, location_id);

/** True when a new sub may still be requested (today or future class dates). */
function canRequestNewSub(lesson: Pick<Lesson, 'date'>): boolean {
  const lessonDate = lesson.date.slice(0, 10);
  return lessonDate >= getCurrentDateOnly();
}

function canActOnLesson(course: Course, instructorId: number): boolean {
  if (!course.venue) return false;
  return currentUser?.id === instructorId || canScheduleFor(course.venue.location_id);
}

function instructorDisplayName(instructor: {
  full_name?: string;
  first_name?: string;
  last_name?: string;
}): string {
  return (
    instructor.full_name?.trim() ||
    `${instructor.first_name ?? ''} ${instructor.last_name ?? ''}`.trim()
  );
}

/** When multiple coverage actions could apply, embed instructor context in labels. */
function needsContextualLabels(actableMainCount: number, actableForOthers: boolean): boolean {
  return actableMainCount > 1 || actableForOthers;
}

export function lessonHasScheduleActions(lesson: Lesson, course: Course): boolean {
  if (!course.venue) return false;

  const hasReneg =
    canRequestNewSub(lesson) &&
    lesson.lesson_assignments.some(
      (la) =>
        (la.instructor_role === 'substitute' || la.instructor_role === 'sub') &&
        la.instructor_id !== null &&
        canActOnLesson(course, la.instructor_id!)
    );

  const hasSubActions = course.course_assignments.some((ca) => {
    if (!canActOnLesson(course, ca.instructor_id)) return false;

    const las = lesson.lesson_assignments.filter(
      (la) => la.requesting_instructor_id === ca.instructor_id
    );
    const hasSub = las.length > 0 ? las.some((la) => la.instructor) : null;

    if (hasSub) return false;
    if (hasSub !== null) return las.length > 0 && las[0].id > 0;
    return canRequestNewSub(lesson);
  });

  return hasReneg || hasSubActions;
}

interface LessonScheduleActionsProps {
  lesson: Lesson;
  course: Course;
  actionCallback: lessonAssignmentActionFn;
  /** Stack actions vertically with full-width buttons (mobile cards). */
  stacked?: boolean;
}

export const LessonCoverageActions = ({
  lesson,
  course,
  actionCallback,
  stacked
}: LessonScheduleActionsProps) => {
  if (!course.venue || !lessonHasScheduleActions(lesson, course)) {
    return null;
  }

  const actableMainAssignments = course.course_assignments.filter((ca) =>
    canActOnLesson(course, ca.instructor_id)
  );
  const contextualLabels = needsContextualLabels(
    actableMainAssignments.length,
    actableMainAssignments.some((ca) => ca.instructor_id !== currentUser?.id)
  );

  const buttonHeight = stacked ? 44 : 28;
  const buttonClass = stacked ? 'w-100' : undefined;

  const buildMainCoverageLine = (
    ca: (typeof course.course_assignments)[number]
  ): ReactElement | null => {
    if (!canActOnLesson(course, ca.instructor_id)) {
      return null;
    }

    const instructor = ca.instructor;
    const name = instructorDisplayName(instructor);
    const isSelf = currentUser?.id === ca.instructor_id;
    const las = lesson.lesson_assignments.filter(
      (la) => la.requesting_instructor_id === ca.instructor_id
    );
    const hasSub = las.length > 0 ? las.some((la) => la.instructor) : null;
    const id = las.length > 0 ? las[0].id : 0;
    const label = isSelf ? 'my' : `${name}'s`;

    if (hasSub) {
      return null;
    }

    if (hasSub !== null && id) {
      return (
        <SimpleButton
          size="sm"
          variant="outline-danger"
          height={buttonHeight}
          className={buttonClass}
          truncate={false}
          onClick={() =>
            actionCallback('delete', {
              id,
              label,
              instructorName: name,
              requesting_instructor_id: ca.instructor_id
            })
          }
        >
          Pending - Cancel Request?
        </SimpleButton>
      );
    }

    if (!canRequestNewSub(lesson)) {
      return null;
    }

    const requestLabel =
      contextualLabels && !isSelf ? `Request sub for ${name}` : 'Request sub';

    const data = {
      lesson_id: lesson.id,
      requesting_instructor_id: ca.instructor_id,
      course_id: course.id,
      location_id: course.venue!.location_id,
      instructorName: name
    };

    return (
      <SimpleButton
        size="sm"
        variant="primary"
        height={buttonHeight}
        className={buttonClass}
        truncate={false}
        onClick={() => actionCallback('create', data)}
      >
        {requestLabel}
      </SimpleButton>
    );
  };

  const buildRenegCoverageLine = (la: (typeof lesson.lesson_assignments)[number]): ReactElement | null => {
    if (
      !canRequestNewSub(lesson) ||
      (la.instructor_role !== 'substitute' && la.instructor_role !== 'sub') ||
      la.instructor_id === null ||
      !canActOnLesson(course, la.instructor_id)
    ) {
      return null;
    }

    const isSelf = currentUser?.id === la.instructor_id;
    const subName = la.instructor?.full_name || 'Substitute';
    const renegLabel = isSelf ? "Can't sub" : `${subName} can't sub`;

    return (
      <SimpleButton
        size="sm"
        variant="primary"
        height={buttonHeight}
        className={buttonClass}
        truncate={false}
        onClick={() => actionCallback('reneg', { id: la.id })}
      >
        {renegLabel}
      </SimpleButton>
    );
  };

  const includedRenegIds = new Set<number>();
  const displayItems = getLessonInstructorDisplay(lesson, course);

  // One row per instructor display item (same order/count as Instructors column).
  // Empty rows preserve alignment when the viewer cannot act (e.g. co-instructor view).
  const rows = displayItems.map((item) => {
    let content: ReactElement | null = null;

    if (item.role === 'Main instructor') {
      const ca = course.course_assignments.find(
        (assignment) => Number(assignment.instructor_id) === item.id
      );
      if (ca) {
        content = buildMainCoverageLine(ca);
      }
    } else {
      const la = lesson.lesson_assignments.find(
        (assignment) =>
          Number(assignment.instructor_id) === item.id &&
          assignment.instructor &&
          (assignment.instructor_role === 'substitute' || assignment.instructor_role === 'sub')
      );
      if (la && !includedRenegIds.has(la.id)) {
        content = buildRenegCoverageLine(la);
        if (content) {
          includedRenegIds.add(la.id);
        }
      }
    }

    return {
      key: `coverage-${item.id}-${item.role}`,
      content
    };
  });

  const visibleRows = stacked ? rows.filter((row) => row.content) : rows;

  if (visibleRows.every((row) => !row.content)) {
    return null;
  }

  return (
    <div
      className={`coverage-actions lesson-instructor-display-stack${
        stacked ? ' coverage-actions--stacked' : ''
      }`}
    >
      {visibleRows.map(({ key, content }) => (
        <div
          key={key}
          className={`coverage-actions-row lesson-instructor-display-row${
            content ? '' : ' coverage-actions-row--empty'
          }`}
          aria-hidden={content ? undefined : true}
        >
          {content}
        </div>
      ))}
    </div>
  );
};

export const LessonScheduleActions = ({
  lesson,
  course,
  actionCallback,
  stacked
}: LessonScheduleActionsProps) => {
  if (!lessonHasScheduleActions(lesson, course)) {
    return null;
  }

  return (
    <div className={stacked ? 'mt-auto pt-3 border-top' : undefined}>
      <LessonCoverageActions
        lesson={lesson}
        course={course}
        actionCallback={actionCallback}
        stacked={stacked}
      />
    </div>
  );
};
